/** Research copy from the final mRehab flyer. Grant periods are not study dates. */
export type ResearchLens = "patient" | "provider";
export const TRIAL = {
  label: "ClinicalTrials.gov",
  href: "https://clinicaltrials.gov/study/NCT04363944?tab=study",
  resultsHref: "https://clinicaltrials.gov/study/NCT04363944?tab=results",
} as const;
export const RESEARCH = {
  title: "The research behind mRehab",
  lead: "Sixteen stroke survivors used it at home for six weeks. Their results are published.",
  measuredLead:
    "Measured on tests clinicians use, as change from each participant's own starting point, in the lab and at home (n = 16):",
  carryOver:
    "Dexterity improved on a task mRehab never trained directly, which suggests the practice carried over to other movements.",
  practice:
    "The people who exercised most often were the most likely to start something new.",
  homeTeaser: "Tested at home by 16 stroke survivors over six weeks.",
  shopTeaser: "Built from NIH-funded research.",
} as const;
export const FIGURES = [
  { value: "9+ years", label: "of research" },
  { value: "10+", label: "published papers" },
  { value: "$1.2M+", label: "in study funding" },
  { value: "50+", label: "study participants" },
] as const;
export const TEASER_FIGURES = [
  { value: "16", label: "stroke survivors" },
  { value: "6 weeks", label: "at home" },
  { value: "9 of 16", label: "returned to daily activities" },
] as const;
export const TIMELINE: ReadonlyArray<{
  title: string;
  meta: string;
  live?: boolean;
  bullets: readonly { text: string; href?: string }[];
}> = [
  {
    title: "Designed with users",
    meta: "Focus groups, 2018",
    bullets: [
      {
        text: "Stroke survivors and therapists helped shape activities based on everyday tasks",
      },
    ],
  },
  {
    title: "Reliability tested",
    meta: "Lab studies, 2019",
    bullets: [
      {
        text: "Every activity was repeated across three sessions at two independent sites",
      },
    ],
  },
  {
    title: "Home validated",
    meta: "NIH funding, 2017 to 2021, $470K",
    bullets: [
      {
        text: "16 stroke survivors completed a 6-week home study using mRehab",
      },
    ],
  },
  {
    title: "Scaling up",
    meta: "NIDILRR funding, 2025 to 2028, $750K",
    live: true,
    bullets: [
      {
        text: "A multi-site study is underway, with a goal of reaching 250 participants",
      },
    ],
  },
];
export type Outcome = { result: string; measure: string; p: string };
// Kinematic measure names are plain-language paraphrases pending paper references.
export const OUTCOMES: readonly Outcome[] = [
  {
    result: "Faster movement when carrying objects",
    measure: "Movement speed during transport",
    p: ".02",
  },
  {
    result: "Smoother, steadier movement",
    measure: "Movement smoothness",
    p: ".003",
  },
  {
    result: "Faster task completion",
    measure: "Wolf Motor Function Test",
    p: ".02",
  },
  {
    result: "Better dexterity",
    measure: "Nine-Hole Peg Test",
    p: ".02",
  },
];
export const EVERYDAY = {
  filled: 9,
  total: 16,
  results: [
    "Resumed activities they could not do before the program",
  ],
} as const;
export const TASKS = [
  "Dressing",
  "Cleaning",
  "Tying shoes",
  "Household tasks",
] as const;
type ResearchImage = {
  src: string | null;
  alt: string;
  width: number;
  height: number;
  objectPosition: string;
  caption: string;
};
export const IMAGES: Record<
  "hero" | "measured" | "everyday",
  ResearchImage
> = {
  hero: {
    src: "/images/research-team-discussion.png",
    alt: "A researcher demonstrating the mRehab mug to stroke survivors and clinicians seated around a table",
    width: 1920,
    height: 1440,
    objectPosition: "35% 55%",
    caption: "A demonstration of the mRehab mug around a table.",
  },
  measured: {
    src: "/images/product/water-pour-activity-new.png",
    alt: "An outstretched arm tipping the mRehab mug in a pouring motion, with the app counting the repetition on the phone inside",
    width: 2780,
    height: 1847,
    objectPosition: "38% 50%",
    caption: "The phone inside the mug tracks each movement during practice.",
  },
  everyday: {
    src: "/images/product/vertical-mug-activity-new.png",
    alt: "A person lifting the mRehab mug onto a box while the app counts the repetition on the phone inside",
    width: 1200,
    height: 900,
    objectPosition: "50% 50%",
    caption: "",
  },
};
